/**
 * Keeps the viewport animating only when that's wanted: the level has
 * something that moves on its own (a VFX ember ring, a model playing a clip)
 * and Realtime is on (as in Unreal). Realtime's default, Automatic, follows the
 * system's reduced-motion preference; On or Off, set by the visitor, beats it.
 *
 * The "Pause viewport when idle" preference decides whether that motion stops
 * after a stretch without input (architecture §5.3). It's off by default.
 *
 * It also sets the world time scale, the one way the world freezes: at 0 while
 * Play is paused, and while editing with Realtime off, so a redraw (a camera
 * move) doesn't jump effects ahead. A running game ticks whatever Realtime
 * says, as Unreal's Play in Editor does.
 */
import type { Unsubscribe } from "../../core/Emitter";
import type { ReadableStore } from "../../core/store";
import type { Animator } from "../../world/components";
import type { EntityRecord } from "../../world/entity";
import type { InferComponentData } from "../../world/schema/defineComponent";
import type { WorldReader } from "../../world/World";
import type { SettingsValues } from "../settings/EditorSettings";

/** The Realtime setting: follow the system, or the visitor's own choice. */
export type RealtimeSetting = "auto" | "on" | "off";

/** Whether the viewport animates on its own: an explicit choice wins over the system's default. */
export function realtimeOn(realtime: RealtimeSetting, reducedMotion: boolean): boolean {
  if (realtime === "auto") return !reducedMotion;
  return realtime === "on";
}

/**
 * Why the viewport is still, when it's still by choice or by the system's
 * preference rather than for lack of anything to animate (the stats say so).
 */
export function idleReason(realtime: RealtimeSetting, reducedMotion: boolean): string | null {
  if (realtime === "off") return "Realtime off";
  if (realtime === "auto" && reducedMotion) return "reduced motion";
  return null;
}

export interface AmbientEffectsOptions {
  readonly world: WorldReader;
  readonly settings: ReadableStore<SettingsValues>;
  /** What's playing, if anything. */
  readonly play: ReadableStore<{ readonly paused: boolean } | null>;
  readonly frameLoop: {
    setWorldTimeScale(scale: number): void;
    setAmbientAnimation(source: string, active: boolean): void;
    setPauseWhenIdle(pause: boolean): void;
  };
  readonly reducedMotion: boolean;
}

/** Each kind of self-moving content, as a frame-loop source, and the components that decide it. */
const SOURCES = [
  {
    source: "vfx",
    components: ["VFX"],
    moves: (entity: EntityRecord) => entity.components.has("VFX"),
  },
  { source: "animation", components: ["Animator", "MeshRenderer"], moves: playsClip },
] as const;

export function driveAmbientEffects({
  world,
  settings,
  play,
  frameLoop,
  reducedMotion,
}: AmbientEffectsOptions): Unsubscribe {
  const update = () => {
    const { realtime, pauseWhenIdle } = settings.getSnapshot();
    const allowed = realtimeOn(realtime, reducedMotion);
    frameLoop.setPauseWhenIdle(pauseWhenIdle);
    frameLoop.setWorldTimeScale(worldTimeScale(allowed, play.getSnapshot()));

    for (const { source, moves } of SOURCES) {
      frameLoop.setAmbientAnimation(source, allowed && anyEntity(world, moves));
    }
  };

  const watched = new Set<string>(SOURCES.flatMap((s) => s.components));
  const unsubscribes = [
    settings.subscribe(update),
    play.subscribe(update),
    world.events.on("world:replaced", update),
    world.events.on("component:changed", ({ type }) => {
      if (watched.has(type)) update();
    }),
  ];
  update();

  return () => {
    for (const unsubscribe of unsubscribes) unsubscribe();
    for (const { source } of SOURCES) frameLoop.setAmbientAnimation(source, false);
  };
}

/** 1 while the world runs, 0 while it's frozen. */
function worldTimeScale(realtime: boolean, playing: { readonly paused: boolean } | null): number {
  if (playing) return playing.paused ? 0 : 1;
  return realtime ? 1 : 0;
}

/** A model with an Animator that's playing forward or backward. */
function playsClip(entity: EntityRecord): boolean {
  const animator = entity.components.get("Animator") as
    InferComponentData<typeof Animator> | undefined;
  return (
    entity.components.has("MeshRenderer") &&
    animator !== undefined &&
    animator.playing &&
    animator.speed !== 0
  );
}

function anyEntity(world: WorldReader, test: (entity: EntityRecord) => boolean): boolean {
  for (const entity of world.getSnapshot().entities.values()) {
    if (test(entity)) return true;
  }
  return false;
}
